/**
 * Functionality for mapbox
 * @version 1.0.0
 */

declare const mapboxgl: any
declare const window: any

class Map {
  container: HTMLElement | null = null
  /**
   * @constructor
   * @param {HTMLElement} container container where all results will be displayed
   * @param {JSON} options option to control the way API will initialize
   */
  constructor(container: HTMLElement, private options: MapOptions) {
    this.container = container

    // Default options
    const defaultsOptions: MapOptions = {
      rootMargin: '0px',
      root: null,
      threshold: 0,
      accessToken: '',
      style: 'mapbox://styles/mapbox/light-v10',
      zoom: 11,
      scroll: false,
      centerLatitude: 0,
      centerLongitude: 0,
      coordinates: [
        {
          latitude: 0,
          longitude: 0,
          icon: '',
          iconHeight: 0,
          iconWidth: 0,
          popup: true,
          autoPopup: true,
          popupHtml: '',
          class: '',
        },
      ],
    }

    this.options = { ...defaultsOptions, ...options }

    // init
    if (this.container) {
      this.setUpObserver(this.container)
    }
  }

  initMap(element: HTMLElement) {
    // catch on document...
    document.addEventListener('hello', function (event) {
      // (1)
    })

    const event = new Event('hello', { bubbles: true }) // (2)
    document.dispatchEvent(event)

    this.getMapboxLoad()
      .then((res) => {
        // eslint-disable-next-line
        mapboxgl.accessToken = this.options.accessToken

        // eslint-disable-next-line
        var map = new mapboxgl.Map({
          container: element,
          center: [this.options.centerLongitude, this.options.centerLatitude],
          zoom: this.options.zoom,
          style: this.options.style,
          dragPan: window.innerWidth > 992 ? true : false,
        })

        map.on('load', () => {
          // Get all localization of markers
          const coordinatesAll = this.options.coordinates
          // console.log(options.centerLongitude+'='+options.centerLatitude);

          // Create geojson for coordinates
          const geojson: Geojson = {
            type: 'FeatureCollection',
            features: [],
          }

          // Add all coordinates to gjson
          coordinatesAll.forEach((key) => {
            // console.log(key)
            const coordinate = {
              type: 'Feature',
              properties: {
                id: key,
                icon: key.icon,
                iconWidth: key.iconWidth,
                iconHeight: key.iconHeight,
                class: key.class,
              },
              geometry: {
                type: 'Point',
                coordinates: [key.longitude, key.latitude],
                popupHtml: key.popupHtml,
              },
            }
            geojson.features.push(coordinate)
          })

          // add markers to map
          geojson.features.forEach(function (marker) {
            const el = document.createElement('div')
            el.className = 'marker'
            el.style.backgroundImage = 'url(' + marker.properties.icon + ')'
            el.style.backgroundSize = 'contain'
            el.style.backgroundPosition = 'center'
            el.style.backgroundRepeat = 'no-repeat'
            el.style.width = marker.properties.iconWidth + 'px'
            el.style.height = marker.properties.iconHeight + 'px'

            // eslint-disable-next-line
            new mapboxgl.Marker(el)
              .setLngLat(marker.geometry.coordinates)
              .setPopup(
                // eslint-disable-next-line
                marker.geometry.popupHtml &&
                  new mapboxgl.Popup({ offset: 25 }).setHTML(
                    marker.geometry.popupHtml,
                  ),
              )
              .addTo(map)

            let scalePercent = map.getZoom() * 6

            if (scalePercent <= 40) {
              scalePercent = 40
            }

            if (scalePercent >= 100) {
              scalePercent = 100
            }

            el.style.backgroundSize = `${scalePercent}%`

            map.on('zoom', () => {
              let scalePercent = map.getZoom() * 5

              if (scalePercent <= 50) {
                scalePercent = 50
              }

              if (scalePercent >= 100) {
                scalePercent = 100
              }

              el.style.backgroundSize = `${scalePercent}%`
            })
          })
        })

        // Find place
        const locations = document.querySelectorAll(`[data-item="location"]`)
        if (locations.length > 0) {
          locations.forEach((item) => {
            item.addEventListener('click', (event) => {
              const new_lng = parseFloat(
                item.getAttribute('data-lng') as string,
              )
              const new_lat = parseFloat(
                item.getAttribute('data-lat') as string,
              )
              const url = item.getAttribute('data-url')

              const locationsActive = document.querySelectorAll(
                `[data-item="location"].active`,
              )

              if (locationsActive.length > 0) {
                locationsActive.forEach((itemActive) => {
                  itemActive.classList.remove('active')
                })
              }

              item.classList.add('active')

              map.flyTo({
                center: [new_lng, new_lat],
                essential: true, // this animation is considered essential with respect to prefers-reduced-motion
                zoom: 13,
              })

              if (this.container && url) {
                console.log(url)

                const legend = document.querySelector(`[data-legend]`)

                fetch(url, {
                  method: 'get',
                })
                  .then((res) => {
                    return res.text()
                  })
                  .then((data) => {
                    console.log(data)

                    setTimeout(() => {
                      // Add new content
                      if (legend) {
                        legend.innerHTML = data
                      }
                    }, 500)
                  })
              }
            })
          })
        }

        map.on('zoom', () => {
          const currentZoom = map.getZoom()
        })

        // disable scroll zoom
        map.scrollZoom.disable()

        // add navigation
        // eslint-disable-next-line
        map.addControl(new mapboxgl.NavigationControl(), 'bottom-right')
      })
      .catch((e) => {
        console.log(e)
      })
  }

  /**
   *  Function to IntersectionObserver
   */
  setUpObserver(container: HTMLElement) {
    if ('IntersectionObserver' in window) {
      const wrapperObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (
              container.getAttribute('data-mapbox-arlady-active') !== 'true'
            ) {
              container.setAttribute('data-mapbox-arlady-active', 'true')
              this.initMap(container)
            }
          }
        })
      }, this.getObserverOptions())

      wrapperObserver.observe(container)
    }
  }

  getObserverOptions(): IntersectionObserverInit {
    const observerOptions: IntersectionObserverInit = {
      root: this.options.root,
      rootMargin: this.options.rootMargin,
      threshold: this.options.threshold,
    }

    return observerOptions
  }

  getMapboxLoad() {
    // (url: String) => Promise[Event]
    function loadLink(url: string) {
      return new Promise((resolve, reject) => {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = url
        link.addEventListener('error', reject)
        link.addEventListener('load', resolve)

        document.head.appendChild(link)
      })
    }
    // (url: String) => Promise[Mapbox]
    function loadScript(url: string) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = url
        script.async = false
        script.addEventListener('error', reject)
        script.addEventListener('load', () => resolve(getMapbox()))

        document.head.appendChild(script)
      })
    }
    // (void) => Promise[Mapbox]
    function getMapbox() {
      return new Promise((resolve, reject) => {
        if (window.mapboxgl) {
          resolve(window.mapboxgl)
        } else {
          reject()
        }
      })
    }
    return new Promise((resolve, reject) =>
      getMapbox()
        .then(resolve)
        .catch(() =>
          Promise.all([
            loadScript(
              'https://api.mapbox.com/mapbox-gl-js/v2.14.1/mapbox-gl.js',
            ),
            loadScript(
              'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-language/v1.0.0/mapbox-gl-language.js',
            ),
            loadLink(
              'https://api.mapbox.com/mapbox-gl-js/v2.14.1/mapbox-gl.css',
            ),
          ])
            .then(([as]) => resolve(as))
            .catch(reject),
        ),
    )
  }
}

type Geojson = {
  type: string
  features: Array<any>
}

export type MapOptions = {
  rootMargin?: string
  root?: Element | null
  threshold?: number
  accessToken: string | undefined
  style?: string
  zoom?: number
  scroll?: boolean
  centerLatitude: number | string
  centerLongitude: number | string
  coordinates: [
    {
      latitude: number
      longitude: number
      icon: string
      iconHeight: number
      iconWidth: number
      popup: boolean
      autoPopup: boolean
      popupHtml: string
      class: string
    },
  ]
}

export default Map
